import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Grid, Paper, CircularProgress, Alert,
  LinearProgress, Chip, Stack, ToggleButton, ToggleButtonGroup,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip,
  ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import BoltIcon from '@mui/icons-material/Bolt';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import SpeedIcon from '@mui/icons-material/Speed';
import { tenantApi } from '../../api/tenantApi';
import {
  StatCard, ChartCard, areaGradient, trendFromSeries, fmtCompact, fmtBytes, ManualRefreshNotice,
} from '../../components/analytics/AnalyticsKit';

function QuotaCard({ label, used, limit, unit = '', pending = false }) {
  const known = used !== null && used !== undefined;
  const pct = known && limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const color = pct >= 90 ? 'error' : pct >= 75 ? 'warning' : 'primary';

  return (
    <Paper elevation={0} sx={{
      p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%',
      boxShadow: '0 1px 3px rgba(15,23,42,.06)',
    }}>
      <Typography variant="body2" color="text.secondary" fontWeight={500} gutterBottom>{label}</Typography>
      <Typography variant="h5" fontWeight={800}>
        {known ? `${used.toLocaleString()}${unit}` : '—'}
        <Typography component="span" variant="body2" color="text.secondary">
          {' / '}{limit > 999998 ? '∞' : limit.toLocaleString()}{unit}
        </Typography>
      </Typography>
      <LinearProgress
        variant="determinate"
        value={pct}
        color={color}
        sx={{ mt: 1, height: 7, borderRadius: 4 }}
      />
      <Typography variant="caption" color={known ? `${color}.main` : 'text.disabled'} sx={{ mt: 0.5, display: 'block' }}>
        {pending ? 'tracked per user session' : `${pct}% used`}
      </Typography>
    </Paper>
  );
}

const PERIODS = [['7d', '7 days'], ['30d', '30 days'], ['90d', '90 days']];

export default function UsageDashboardPage() {
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    tenantApi.getMyUsage(period)
      .then((d) => { if (active) { setData(d); setError(null); } })
      .catch((e) => { if (active) { setError(e.message); } })
      .finally(() => { if (active) { setLoading(false); } });
    return () => { active = false; };
  }, [period]);

  if (loading && !data) { return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>; }
  if (error) { return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>; }
  if (!data) { return null; }

  const { subscription, quotas, current, apiUsage, dailyUsage = [], storageBytes } = data;
  const reqTrend = trendFromSeries(dailyUsage, 'apiRequests');
  const reqSpark = dailyUsage.map((d) => ({ v: d.apiRequests }));
  const storageSeries = dailyUsage.filter((d) => d.storageBytes !== null && d.storageBytes !== undefined);
  const periodLabel = (PERIODS.find(([k]) => k === period) || [])[1] || period;

  return (
    <Box sx={{ p: 3, maxWidth: 1240 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5} flexWrap="wrap" rowGap={1}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography variant="h5" fontWeight={800}>Usage & Limits</Typography>
          <Chip label={subscription?.plan?.toUpperCase()} color="primary" sx={{ fontWeight: 700 }} />
          <Chip
            label={subscription?.status}
            color={subscription?.status === 'ACTIVE' ? 'success' : 'warning'}
            variant="outlined"
          />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <ManualRefreshNotice />
          <ToggleButtonGroup size="small" exclusive value={period}
            onChange={(_, v) => { if (v) { setPeriod(v); } }}>
            {PERIODS.map(([k, l]) => <ToggleButton key={k} value={k}>{l}</ToggleButton>)}
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      {/* API usage stats */}
      <Grid container spacing={2} mb={2.5}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label={`API Requests (${periodLabel})`} value={fmtCompact(apiUsage?.totalRequests ?? 0)}
            trend={reqTrend} spark={reqSpark} icon={<BoltIcon />} color="#1976d2" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Error Rate" value={`${apiUsage?.errorRate ?? 0}%`}
            sub={`${fmtCompact(apiUsage?.errors ?? 0)} errors (4xx + 5xx)`}
            icon={<ErrorOutlineIcon />}
            color={(apiUsage?.errorRate ?? 0) > 1 ? '#c62828' : '#2e7d32'} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Inventory Operations" value={fmtCompact(apiUsage?.inventoryOps ?? 0)}
            sub="creates, updates, adjustments" icon={<Inventory2Icon />} color="#00897b" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Data Transferred" value={fmtBytes(apiUsage?.bytesOut ?? 0)}
            sub={storageBytes !== null && storageBytes !== undefined ? `${fmtBytes(storageBytes)} stored` : 'API responses'}
            icon={<SwapVertIcon />} color="#7b1fa2" />
        </Grid>
      </Grid>

      {/* Daily usage charts */}
      <Grid container spacing={2} mb={2.5}>
        <Grid item xs={12} md={storageSeries.length > 1 ? 8 : 12}>
          <ChartCard title={`Daily API Activity — last ${periodLabel}`} subtitle="every request is metered, reads included">
            {dailyUsage.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={dailyUsage}>
                  <defs>
                    {areaGradient('uReq', '#1976d2')}
                    {areaGradient('uErr', '#d32f2f')}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,.08)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)}
                    tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={40} />
                  <ChartTooltip />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="apiRequests" name="Requests" stroke="#1976d2"
                    strokeWidth={2} fill="url(#uReq)" dot={false} />
                  <Area type="monotone" dataKey="errors" name="Errors" stroke="#d32f2f"
                    strokeWidth={2} fill="url(#uErr)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">No usage recorded in this window yet.</Typography>
              </Box>
            )}
          </ChartCard>
        </Grid>
        {storageSeries.length > 1 && (
          <Grid item xs={12} md={4}>
            <ChartCard title="Storage Trend" subtitle="daily snapshot (files + database)">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={storageSeries}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,.08)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)}
                    tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={54}
                    tickFormatter={(v) => fmtBytes(v)} />
                  <ChartTooltip formatter={(v) => fmtBytes(v)} />
                  <Bar dataKey="storageBytes" name="Storage" fill="#7b1fa2" radius={[5, 5, 0, 0]} maxBarSize={26} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        )}
      </Grid>

      {/* Quota meters */}
      <Typography variant="subtitle2" fontWeight={700} mb={1}>Resource Quotas</Typography>
      <Grid container spacing={2} mb={2.5}>
        <Grid item xs={6} md={3}>
          <QuotaCard label="Inventory Items" used={current?.itemCount ?? 0} limit={quotas?.maxItems ?? 5000} />
        </Grid>
        <Grid item xs={6} md={3}>
          <QuotaCard label="Warehouses" used={current?.warehouseCount ?? 0} limit={quotas?.maxWarehouses ?? 1} />
        </Grid>
        <Grid item xs={6} md={3}>
          <QuotaCard label="API Keys" used={current?.apiKeyCount ?? 0} limit={quotas?.maxApiKeys ?? 3} />
        </Grid>
        <Grid item xs={6} md={3}>
          <QuotaCard label="Users" used={current?.userCount} limit={quotas?.maxUsers ?? 5}
            pending={current?.userCount === null || current?.userCount === undefined} />
        </Grid>
      </Grid>

      {/* Plan details */}
      <Grid container spacing={2} mb={2.5}>
        <Grid item xs={12} md={4}>
          <StatCard label="Rate Limit" value={`${(quotas?.apiRateLimitPerMinute ?? 100).toLocaleString()}/min`}
            sub="requests allowed per minute" icon={<SpeedIcon />} color="#ef6c00" />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard label="Current Plan" value={subscription?.plan ?? '—'}
            sub={subscription?.renewalDate ? `renews ${subscription.renewalDate}` : 'no renewal date'}
            color="#1976d2" />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard label="License Status" value={subscription?.status ?? '—'}
            color={subscription?.status === 'ACTIVE' ? '#2e7d32' : '#ef6c00'} />
        </Grid>
      </Grid>

      {/* API usage by key */}
      {apiUsage?.byKey?.length > 0 && (
        <ChartCard title="Usage by API Key">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Key Name</TableCell>
                <TableCell>Key Prefix</TableCell>
                <TableCell align="right">Requests</TableCell>
                <TableCell align="right">Errors</TableCell>
                <TableCell>Last Used</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {apiUsage.byKey.map((k) => (
                <TableRow key={k.keyId}>
                  <TableCell>{k.name}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{k.keyPrefix}…</TableCell>
                  <TableCell align="right">{k.requests.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ color: k.errors > 0 ? 'error.main' : 'inherit' }}>
                    {k.errors.toLocaleString()}
                  </TableCell>
                  <TableCell>{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ChartCard>
      )}
    </Box>
  );
}
